import React, { useMemo, useState } from 'react';
import {
  Box, Typography, Accordion, AccordionSummary, AccordionDetails,
  List, ListItemButton, ListItemText, Chip, Divider, Paper, Stack,
} from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import WarningIcon from '@mui/icons-material/Warning';
import BuildIcon from '@mui/icons-material/Build';
import api from '../../../../services/api';
import { useTopologyBase } from './topologyApi';
import { SUPERFICIE } from '../snapshot/estilo';

const SEVERITY_COLOR = {
  high: 'error',
  medium: 'warning',
  low: 'info',
  info: 'default',
};

// rótulos amigáveis; kinds sem rótulo caem no próprio kind
const KIND_LABELS = {
  ip_duplicated: 'IP duplicado',
  p2p_overpopulated: '/30 com 3+ hosts',
  bgp_peer_unresolved: 'BGP peer externo',
  bgp_asn_mismatch: 'BGP ASN mismatch',
  default_nexthop_unresolved: 'Default sem next-hop',
  ospf_area_mismatch: 'OSPF area mismatch',
  hard_no_firewall: 'RouterBoard sem firewall',
  hard_no_input: 'Sem proteção chain=input',
  hard_svc_open: 'Serviço de gerência sem restrição',
  hard_open_dns: 'DNS open resolver',
  hard_romon: 'RoMON habilitado',
  hard_ssh_weak: 'SSH sem strong-crypto',
  hard_mac_server: 'MAC-server sem restrição',
  hard_telnet: 'Telnet habilitado',
  hard_vty_telnet: 'VTY aceita Telnet',
  hard_snmp_public: 'SNMP community public',
  hard_no_aaa: 'Sem AAA',
  hard_ssh_v1: 'SSHv1 aceito',
  hard_pwd_enc: 'Sem password-encryption',
  hard_no_urpf: 'Sem uRPF (BCP38)',
  hard_no_syslog: 'Sem syslog remoto',
  hard_no_ntp: 'Sem NTP',
  bgp_no_in_filter: 'eBGP sem filtro de entrada',
  bgp_no_out_filter: 'eBGP sem filtro de saída',
  bgp_no_maxprefix: 'eBGP sem max-prefix',
  bgp_no_rpki: 'Borda sem RPKI',
  bgp_no_bogon: 'Sem anti-bogon',
  ref_undefined: 'Route-policy não definida',
  ospf_redis_unfiltered: 'Redistribuição OSPF sem filtro',
  ospf_no_auth: 'OSPF sem autenticação',
  spof: 'Ponto único de falha (SPOF)',
};

const CAT_ALL = '__all__';

/**
 * Achados agrupados por tipo.
 *
 * O mapa manda só o RESUMO — 24 grupos com contagem, gravidade e quantos
 * equipamentos cada um atinge. As 1.797 issues inteiras eram 443 KB no payload
 * pra a tela agrupar por tipo no cliente antes de mostrar; agora o servidor
 * agrupa e a lista de um grupo vem quando ele é aberto.
 *
 * O resumo tem que responder sozinho "quantos, quão grave, quantos
 * equipamentos" — se obrigasse a expandir pra saber isso, não teria sobrado
 * ganho nenhum.
 */
export default function IssuesPanel({ resumo, total, onIssueClick }) {
  const base = useTopologyBase();
  const [cat, setCat] = useState(CAT_ALL);
  const [detalhes, setDetalhes] = useState({});   // kind -> issues[]
  const [carregando, setCarregando] = useState(null);

  const cats = useMemo(() => {
    const s = new Set();
    for (const g of resumo || []) if (g.cat) s.add(g.cat);
    return Array.from(s).sort();
  }, [resumo]);

  const grupos = useMemo(
    () => (resumo || []).filter((g) => cat === CAT_ALL || g.cat === cat),
    [resumo, cat],
  );
  const filtradoTotal = grupos.reduce((a, g) => a + g.total, 0);

  const abrir = (kind) => {
    if (detalhes[kind] || carregando === kind) return;
    setCarregando(kind);
    api.get(`${base}/issues?kind=${encodeURIComponent(kind)}&limite=500`)
      .then((r) => setDetalhes((p) => ({ ...p, [kind]: r.data?.issues || [] })))
      .catch(() => setDetalhes((p) => ({ ...p, [kind]: [] })))
      .finally(() => setCarregando(null));
  };

  return (
    <Paper elevation={0} sx={{ p: 2, maxHeight: '100%', overflow: 'auto', ...SUPERFICIE,
      // Accordion tambem e Paper: sem isso cada grupo de achado volta ao escuro.
      '& .MuiAccordion-root': { bgcolor: '#ffffff' },
      '& .MuiDivider-root': { borderColor: '#e2e8f0' },
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <WarningIcon fontSize="small" color="error" />
        <Typography variant="subtitle2" fontWeight={600}>
          Achados ({filtradoTotal}{cat !== CAT_ALL ? ` de ${total || 0}` : ''})
        </Typography>
      </Box>

      {cats.length > 0 && (
        <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
          <Chip
            label="tudo"
            size="small"
            variant={cat === CAT_ALL ? 'filled' : 'outlined'}
            onClick={() => setCat(CAT_ALL)}
          />
          {cats.map((c) => (
            <Chip
              key={c}
              label={c}
              size="small"
              variant={cat === c ? 'filled' : 'outlined'}
              onClick={() => setCat(c)}
            />
          ))}
        </Stack>
      )}

      <Divider sx={{ mb: 1 }} />
      {grupos.length === 0 && (
        <Typography variant="body2" color="success.main">
          Nenhum achado ✓
        </Typography>
      )}

      {grupos.map((g) => {
        const lista = detalhes[g.kind];
        return (
          <Accordion
            key={`${g.kind}-${g.severity}`}
            disableGutters
            onChange={(_, aberto) => aberto && abrir(g.kind)}
            sx={{ boxShadow: 'none', '&:before': { display: 'none' } }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Chip
                label={g.total}
                size="small"
                color={SEVERITY_COLOR[g.severity] || 'default'}
                sx={{ mr: 1 }}
              />
              <Typography variant="body2" sx={{ flex: 1 }}>
                {KIND_LABELS[g.kind] || g.kind}
              </Typography>
              {/* quantos equipamentos, não só quantos achados: 37 IPs
                  duplicados em 176 hosts é outra história que 37 em 37 */}
              <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>
                {g.hosts_afetados} host{g.hosts_afetados === 1 ? '' : 's'}
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0 }}>
              {carregando === g.kind && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
                  <CircularProgress size={16} />
                </Box>
              )}
              <List dense disablePadding>
                {(lista || g.amostra || []).slice(0, 50).map((i, idx) => (
                  <ListItemButton key={idx} onClick={() => onIssueClick && onIssueClick(i)}>
                    <ListItemText
                      primary={i.detail}
                      secondary={
                        (i.fix || i.ref) ? (
                          <Box component="span" sx={{ display: 'block', mt: 0.3 }}>
                            {i.fix && (
                              <Box
                                component="span"
                                sx={{
                                  display: 'flex', alignItems: 'center', gap: 0.5,
                                  color: 'success.main',
                                }}
                              >
                                <BuildIcon sx={{ fontSize: 12 }} />
                                {i.fix}
                              </Box>
                            )}
                            {i.ref && (
                              <Box component="span" sx={{ color: 'text.disabled', fontSize: 10 }}>
                                {i.ref}
                              </Box>
                            )}
                          </Box>
                        ) : null
                      }
                      primaryTypographyProps={{
                        variant: 'caption',
                        sx: { wordBreak: 'break-word' },
                      }}
                      secondaryTypographyProps={{ component: 'span', variant: 'caption' }}
                    />
                  </ListItemButton>
                ))}
                {(lista || []).length > 50 && (
                  <ListItemText sx={{ pl: 2 }} secondary={`... +${lista.length - 50}`} />
                )}
                {!lista && carregando !== g.kind && g.total > (g.amostra || []).length && (
                  <ListItemText sx={{ pl: 2 }}
                    secondary={`mostrando ${(g.amostra || []).length} de ${g.total} — abra pra carregar o resto`} />
                )}
              </List>
            </AccordionDetails>
          </Accordion>
        );
      })}
    </Paper>
  );
}
